// Story 8.5 (ADO-2014), D-8, D-13, D-14 and D-17: the pure cut-off display rules. No React.
//
// The seconds left are rounded UP (D-17): with whole-minute deadlines, the pill then reads
// the banner deadline minus the HH:MM clock the Shopper sees, and "0m 00s" appears at the
// deadline, not during the last open second. Every reading covers a range closed at the
// top, so an exact boundary belongs to the lower band: 3600s reads "60m 00s" (never
// "1h 0m") and 86400s reads "24h 0m" (never "1d 0h").
//
// Every date and time is shown in the depot's zone (Europe/Dublin), never the browser's,
// through `Intl.DateTimeFormat('en-IE')` (the locale `shared/money.ts` uses). The copy
// follows the approved Figma: "8m 42s", "0m 05s", "2h 38m", "3d 0h", "Wed 23 Sep, 2:00pm".

/** The trading zone the cut-off is set in (BWG and SL1.0). */
export const CUT_OFF_TIME_ZONE = 'Europe/Dublin';

const DISPLAY_LOCALE = 'en-IE';

export type CutOffTone = 'calm' | 'warning' | 'urgent';

/** `warning` at or under 60 minutes, from the "60m 00s" reading (story decision D-17; the
 *  Figma annotation's bands, D-13, which supersede the UX spec's 14 minutes for this pill). */
export const WARNING_AT_OR_UNDER_SECONDS = 60 * 60;
/** `urgent` under 10 minutes, from the "9m 59s" reading; "10m 00s" is still `warning`. */
export const URGENT_UNDER_SECONDS = 10 * 60;

/** At or under this the countdown shows minutes and seconds ("60m 00s", "8m 42s") and
 *  ticks every second. Equal to `WARNING_AT_OR_UNDER_SECONDS` today, but a display rule,
 *  not a tone rule. */
export const SECONDS_SHOWN_AT_OR_UNDER_SECONDS = 60 * 60;

/** Over this the countdown shows days and hours ("1d 1h", "3d 0h"); at exactly this it
 *  reads "24h 0m". */
export const DAYS_SHOWN_OVER_SECONDS = 24 * 60 * 60;

/** Whole seconds left, rounded up and clamped at zero (D-17). */
function wholeSecondsLeft(secondsLeft: number): number {
  return Math.max(0, Math.ceil(secondsLeft));
}

/** The tone for the seconds left (never parsed from a string). It follows the reading:
 *  a fractional value is rounded up first, as the copy is. */
export function cutOffTone(secondsLeft: number): CutOffTone {
  const seconds = wholeSecondsLeft(secondsLeft);
  if (seconds < URGENT_UNDER_SECONDS) return 'urgent';
  if (seconds <= WARNING_AT_OR_UNDER_SECONDS) return 'warning';
  return 'calm';
}

interface ZonedParts {
  readonly weekday: string;
  readonly weekdayLong: string;
  readonly day: string;
  readonly month: string;
  readonly year: string;
  readonly time: string;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: string, options: Intl.DateTimeFormatOptions, key: string) {
  const cacheKey = `${timeZone}|${key}`;
  let formatter = formatters.get(cacheKey);
  if (formatter === undefined) {
    formatter = new Intl.DateTimeFormat(DISPLAY_LOCALE, { timeZone, ...options });
    formatters.set(cacheKey, formatter);
  }
  return formatter;
}

function zonedParts(instantMs: number, timeZone: string): ZonedParts {
  const parts = formatterFor(
    timeZone,
    {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
    'parts',
  ).formatToParts(instantMs);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  const dayPeriod = get('dayPeriod')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return {
    weekday: get('weekday'),
    weekdayLong: formatterFor(timeZone, { weekday: 'long' }, 'long').format(instantMs),
    day: get('day'),
    // en-IE abbreviates September as "Sept"; the design uses three letters.
    month: get('month').slice(0, 3),
    year: get('year'),
    time: `${get('hour')}:${get('minute')}${dayPeriod}`,
  };
}

/** "Wed 23 Sep, 2:00pm", in `timeZone` (Dublin by default). */
export function formatDeadline(deadlineIso: string, timeZone = CUT_OFF_TIME_ZONE): string {
  const parts = zonedParts(Date.parse(deadlineIso), timeZone);
  return `${parts.weekday} ${parts.day} ${parts.month}, ${parts.time}`;
}

/** "Today 2:00pm" when the deadline falls on the same local date as `nowMs` in `timeZone`
 *  (Dublin by default), else `formatDeadline`'s "Thu 24 Sep, 10:00am": the "Next cut-off"
 *  list (Story 8.5, D-20). */
export function formatDeadlineFromToday(
  deadlineIso: string,
  nowMs: number,
  timeZone = CUT_OFF_TIME_ZONE,
): string {
  const parts = zonedParts(Date.parse(deadlineIso), timeZone);
  return isSameDate(parts, zonedParts(nowMs, timeZone))
    ? `Today ${parts.time}`
    : formatDeadline(deadlineIso, timeZone);
}

/** "4:46pm" when the instant falls on the same local date as `nowMs` in `timeZone` (Dublin by
 *  default), else "4:46pm on 14 Oct 2026": Story 8.3's sent time on cards 10 and 11 (D-27). */
export function formatTimeWithDateUnlessSameDay(
  instantIso: string,
  nowMs: number,
  timeZone = CUT_OFF_TIME_ZONE,
): string {
  const parts = zonedParts(Date.parse(instantIso), timeZone);
  return isSameDate(parts, zonedParts(nowMs, timeZone))
    ? parts.time
    : `${parts.time} on ${parts.day} ${parts.month} ${parts.year}`;
}

/** "Wed 23 Sep 2026 at 1:51pm", in `timeZone` (Dublin by default): Story 8.6's order
 *  confirmation, which shares these zoned parts. */
export function formatDateTimeWithYear(instantIso: string, timeZone = CUT_OFF_TIME_ZONE): string {
  const parts = zonedParts(Date.parse(instantIso), timeZone);
  return `${parts.weekday} ${parts.day} ${parts.month} ${parts.year} at ${parts.time}`;
}

/** The cut-off status, structurally the basket client's union (`serverNow` not needed). */
export type CutOffDisplayStatus =
  | { readonly state: 'NONE' }
  | { readonly state: 'OPEN'; readonly deadline: string }
  | {
      readonly state: 'PASSED';
      readonly passedDeadline: string;
      readonly nextDeadline: string | null;
    };

/** The deadline the pill counts down to and the banner shows (D-14): OPEN's deadline, or
 *  once today's cut-offs have passed, the next one. `null` when there is nothing to show. */
export function countdownDeadline(status: CutOffDisplayStatus | null): string | null {
  if (status === null) return null;
  if (status.state === 'OPEN') return status.deadline;
  if (status.state === 'PASSED') return status.nextDeadline;
  return null;
}

/** Whether a pill renders for this status and countdown (the pill's own rule). A container
 *  that exists only for the pill (a landing tile's `.tileCutOff`) renders on the same rule, so
 *  it is never left empty. */
export function isCutOffShown(
  status: CutOffDisplayStatus | null,
  countdown: { readonly secondsLeft: number } | null,
): boolean {
  return countdownDeadline(status) !== null && countdown !== null;
}

const UNIT_SUFFIX = { day: 'd', hour: 'h', minute: 'm', second: 's' } as const;
type CountdownUnit = keyof typeof UNIT_SUFFIX;

interface CountdownParts {
  /** The larger unit, then the smaller one: days and hours, hours and minutes, or
   *  minutes and seconds. */
  readonly units: readonly [CountdownUnit, CountdownUnit];
  readonly values: readonly [number, number];
}

/** Splits the seconds left into the two units shown (D-17). The seconds are rounded up and
 *  clamped at zero, and so is the smaller unit shown: over an hour the total minutes are
 *  rounded up (3601s is "1h 1m"), over a day the total hours (86401s is "1d 1h"). */
function countdownParts(secondsLeft: number): CountdownParts {
  const seconds = wholeSecondsLeft(secondsLeft);
  if (seconds > DAYS_SHOWN_OVER_SECONDS) {
    const totalHours = Math.ceil(seconds / 3600);
    return { units: ['day', 'hour'], values: [Math.floor(totalHours / 24), totalHours % 24] };
  }
  if (seconds > SECONDS_SHOWN_AT_OR_UNDER_SECONDS) {
    const totalMinutes = Math.ceil(seconds / 60);
    return {
      units: ['hour', 'minute'],
      values: [Math.floor(totalMinutes / 60), totalMinutes % 60],
    };
  }
  return { units: ['minute', 'second'], values: [Math.floor(seconds / 60), seconds % 60] };
}

/** The pill's countdown copy. Seconds are two digits and nothing else is padded (D-14):
 *  "8m 05s", "0m 00s", "60m 00s", "8h 5m", "24h 0m", "3d 0h". */
export function formatCountdown(secondsLeft: number): string {
  const { units, values } = countdownParts(secondsLeft);
  return `${unitCopy(units[0], values[0])} ${unitCopy(units[1], values[1])}`;
}

function unitCopy(unit: CountdownUnit, value: number): string {
  const digits = unit === 'second' ? String(value).padStart(2, '0') : String(value);
  return `${digits}${UNIT_SUFFIX[unit]}`;
}

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** The pill's full accessible name, e.g. "Cut-off in 8 minutes 42 seconds". */
export function countdownLabel(secondsLeft: number): string {
  const { units, values } = countdownParts(secondsLeft);
  return `Cut-off in ${plural(values[0], units[0])} ${plural(values[1], units[1])}`;
}

/** Milliseconds from `nowMs` until the next local midnight in `timeZone` (DST-aware). */
export function msUntilNextLocalMidnight(nowMs: number, timeZone = CUT_OFF_TIME_ZONE): number {
  // Step forward by whole hours to the first instant on a later local date, then back to
  // that date's first minute. Works across 23- and 25-hour days.
  const today = zonedParts(nowMs, timeZone);
  let probe = nowMs - (nowMs % 60_000);
  while (isSameDate(zonedParts(probe, timeZone), today)) probe += 3_600_000;
  // `probe` is within the first hour of the next local day; walk back minute by minute.
  while (!isSameDate(zonedParts(probe - 60_000, timeZone), today)) probe -= 60_000;
  return probe - nowMs;
}

function isSameDate(left: ZonedParts, right: ZonedParts): boolean {
  return left.year === right.year && left.month === right.month && left.day === right.day;
}
